import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserTypeBadge } from "./UserTypeBadge";
import { UnifiedUser } from "@/types/unified-user";
import { MonitorSmartphone, Mail, Building2, Calendar, Eye, UserCog, Trash2, Shield } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ActionsDropdown, ActionItem } from "@/components/ui/actions-dropdown";

interface UnifiedUserCardProps {
  user: UnifiedUser;
  onClick: () => void;
}

export function UnifiedUserCard({ user, onClick }: UnifiedUserCardProps) {
  const actions: ActionItem[] = [
    {
      label: "View Details",
      icon: Eye,
      onClick: () => onClick(),
    },
    // Role management - show for users with console access or can get console access
    ...(user.type === 'console' || user.type === 'both' 
      ? [{
          label: "Manage Roles",
          icon: Shield,
          onClick: () => console.log("Manage roles for:", user.id),
        }]
      : [{
          label: "Assign Role",
          icon: Shield,
          onClick: () => console.log("Assign role to:", user.id),
        }]
    ),
    {
      label: "Edit User",
      icon: UserCog,
      onClick: () => console.log("Edit user:", user.id),
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: () => console.log("Delete user:", user.id),
      destructive: true,
    },
  ];

  return (
    <Card disableHover className="cursor-pointer" onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0 mr-2">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg truncate">{user.name}</h3>
              <UserTypeBadge type={user.type} />
            </div>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <span className="truncate">{user.email}</span>
            </p>
          </div>
          
          <div className="flex items-start gap-2 flex-shrink-0">
            <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
              {user.status}
            </Badge>
            <div onClick={(e) => e.stopPropagation()}>
              <ActionsDropdown actions={actions} />
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {user.department && (
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <span>{user.department}</span>
            </div>
          )}
          
          {user.deviceCount > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <MonitorSmartphone className="w-4 h-4 text-muted-foreground" />
              <span>{user.deviceCount} {user.deviceCount === 1 ? 'device' : 'devices'}</span>
            </div>
          )}

          {user.lastActive && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Active {formatDistanceToNow(new Date(user.lastActive), { addSuffix: true })}</span>
            </div>
          )}
        </div>

        {user.roles && user.roles.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {user.roles.map((userRole) => (
              <Badge key={userRole.id} variant="outline" className="text-xs">
                {userRole.role?.name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
